import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { Toaster } from 'sonner';

import './index.css';
import { AppLayout } from './components/AppLayout';
import { HomeRedirect } from './components/HomeRedirect';
import { LoginPage } from './pages/LoginPage';
import { ProxiesPage } from './pages/admin/ProxiesPage';
import { AccountsPage } from './pages/admin/AccountsPage';
import { GroupsPage } from './pages/admin/GroupsPage';
import { FiltersPage } from './pages/admin/FiltersPage';
import { LeadsPage } from './pages/admin/LeadsPage';
import { TenantConfigPage } from './pages/admin/TenantConfigPage';
import { SalesUsersPage } from './pages/admin/SalesUsersPage';
import { DashboardPage } from './pages/admin/DashboardPage';
import { LlmUsagePage } from './pages/admin/LlmUsagePage';
import { AuditLogPage } from './pages/admin/AuditLogPage';
import { RawMessagesPage } from './pages/admin/RawMessagesPage';
import { DeadLettersPage } from './pages/admin/DeadLettersPage';
import { DiscoveryPage } from './pages/admin/DiscoveryPage';
import { ExpansionPage } from './pages/admin/ExpansionPage';
import { NoiseAnchorsPage } from './pages/admin/NoiseAnchorsPage';
import {
  L1PassedTracesPage,
  L2PassedTracesPage,
  L3DroppedTracesPage,
} from './pages/admin/CascadeTracesPage';
import { SystemConfigPage } from './pages/admin/SystemConfigPage';
import { SalesLeadsPage } from './pages/sales/SalesLeadsPage';
import { RequireAuth } from './lib/RequireAuth';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      // Default page is role-aware — sales reps land on their workspace,
      // admin lands on the proxies page (first admin entry).
      { index: true, element: <HomeRedirect /> },
      { path: 'admin/dashboard', element: <DashboardPage /> },
      { path: 'admin/proxies', element: <ProxiesPage /> },
      { path: 'admin/accounts', element: <AccountsPage /> },
      { path: 'admin/groups', element: <GroupsPage /> },
      { path: 'admin/discovery', element: <DiscoveryPage /> },
      { path: 'admin/expansion', element: <ExpansionPage /> },
      { path: 'admin/filters', element: <FiltersPage /> },
      { path: 'admin/tenant-config', element: <TenantConfigPage /> },
      { path: 'admin/leads', element: <LeadsPage /> },
      { path: 'admin/sales-users', element: <SalesUsersPage /> },
      { path: 'admin/llm-usage', element: <LlmUsagePage /> },
      { path: 'admin/audit', element: <AuditLogPage /> },
      { path: 'admin/raw-messages', element: <RawMessagesPage /> },
      { path: 'admin/dead-letters', element: <DeadLettersPage /> },
      { path: 'admin/noise-anchors', element: <NoiseAnchorsPage /> },
      { path: 'admin/cascade/l1-passed', element: <L1PassedTracesPage /> },
      { path: 'admin/cascade/l2-passed', element: <L2PassedTracesPage /> },
      { path: 'admin/cascade/l3-dropped', element: <L3DroppedTracesPage /> },
      { path: 'admin/system-config', element: <SystemConfigPage /> },
      { path: 'sales/leads', element: <SalesLeadsPage /> },
    ],
  },
]);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      {/* Global toast host. Position bottom-right for less collision with
          the WS "new lead" flash banner in the sales workspace. */}
      <Toaster
        position="bottom-right"
        richColors
        closeButton
        duration={5000}
      />
    </QueryClientProvider>
  </React.StrictMode>,
);
